import React, { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { formatNumber } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryHexColor } from "@/utils/categoryColors";
import { CategoryType } from "@/types";
import { darkenColor } from "@/utils/colorUtils";

import { Measure } from "@/types";

interface DocumentedMeasuresBarChartProps {
  documentedMeasurements: (Measure & {
    batchTitle?: string;
  })[];
}

const DocumentedMeasuresBarChart: React.FC<DocumentedMeasuresBarChartProps> = ({
  documentedMeasurements,
}) => {
  const { language, t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleMouseEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  const totalCosts = useMemo(() => {
    return documentedMeasurements.reduce(
      (acc, item) => {
        acc.net += Number(item.totalNet) || 0;
        acc.gross += Number(item.totalGross) || 0;
        return acc;
      },
      { net: 0, gross: 0 }
    );
  }, [documentedMeasurements]);

  const prepareDocumentedChartData = (
    measurements: (Measure & {
      batchTitle?: string;
    })[]
  ): Array<{
    category: CategoryType;
    displayName: string;
    value: number;
    color: string;
  }> => {
    const categoryTotals: Partial<Record<CategoryType, number>> = {};

    measurements.forEach((item) => {
      const amount = parseFloat(item.totalNet.toString());

      if (!isNaN(amount)) {
        categoryTotals[item.category] =
          (categoryTotals[item.category] || 0) + amount;
      }
    });

    return Object.keys(categoryTotals)
      .map((category) => ({
        category: category as CategoryType,
        displayName: t(`categories.${category}`) || category,
        value: categoryTotals[category],
        color: getCategoryHexColor(category as CategoryType),
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  };

  const chartData = prepareDocumentedChartData(documentedMeasurements);

  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">
          {t("documentedMeasures.noData")}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-col items-start text-sm">
        <div className="mb-2">
          <span className="font-medium text-muted-foreground">
            {t("documentedMeasures.totalNetCosts")}
          </span>
          <span className="font-semibold ml-2">
            {new Intl.NumberFormat(language === "de" ? "de-DE" : "en-US", {
              style: "currency",
              currency: "EUR",
            }).format(totalCosts.net)}
          </span>
        </div>
        {totalCosts.gross > 0 && (
          <div>
            <span className="font-medium text-muted-foreground">
              {t("documentedMeasures.totalGrossCosts")}
            </span>
            <span className="font-semibold ml-2">
              {new Intl.NumberFormat(language === "de" ? "de-DE" : "en-US", {
                style: "currency",
                currency: "EUR",
              }).format(totalCosts.gross)}
            </span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 60 }}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          {!isMobile && (
            <XAxis
              dataKey="displayName"
              angle={-45}
              textAnchor="end"
              height={130}
              tick={{ fontSize: 12 }}
              interval={0}
              tickLine={false}
            />
          )}
          <YAxis
            width={100}
            tickFormatter={(value) => `€${formatNumber(value, language)}`}
            tick={{ fontSize: 12 }}
            label={{
              value: t("documentedMeasures.net"),
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle" },
              dy: 50,
              dx: 0,
            }}
          />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const category = payload[0].payload.category;
                const categoryDescription = t(
                  `categories.descriptions.${category}`
                );
                return (
                  <div className="bg-white p-3 border rounded shadow-sm max-w-[300px]">
                    <p className="text-sm font-medium mb-1">
                      {payload[0].payload.displayName}
                    </p>
                    <p className="text-sm text-gray-600 mb-2 whitespace-normal break-words">
                      {categoryDescription}
                    </p>
                    <p className="text-sm font-medium">
                      {new Intl.NumberFormat(
                        language === "de" ? "de-DE" : "en-US",
                        {
                          style: "currency",
                          currency: "EUR",
                        }
                      ).format(Number(payload[0].value))}
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar
            dataKey="value"
            radius={[4, 4, 0, 0]}
            onMouseEnter={handleMouseEnter}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  activeIndex === index
                    ? darkenColor(entry.color, 0.2)
                    : entry.color
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default DocumentedMeasuresBarChart;
